import { A } from "@solidjs/router";
import { StDropdownItem } from "@storytell/ui/dropdown";
import {
  TbBuildingCommunity,
  TbCreditCard,
  TbExternalLink,
  TbGift,
  TbHelp,
  TbLogout,
  TbPencil,
  TbSettings,
  TbTerminal2,
  TbUserPlus,
  TbWorld,
} from "solid-icons/tb";
import { type Component, Show } from "solid-js";
import { UserAccountOrgSwitcher } from "~/components/common/internal/UserAccountOrgSwitcher";
import { SUPPORT_MAILTO } from "~/constants/urls";
import { useOrgScope } from "~/hooks/useOrgScope";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

const ACCOUNT_MENU_LINK_ROW =
  "group cursor-pointer flex items-center gap-2.5 rounded-md px-2 py-2 text-primary-text transition-colors hover:bg-canvas-hover hover:text-brand";
const ACCOUNT_MENU_SVG_ICON = "size-4 shrink-0";

/**
 * Trailing "leaves the app" marker. The arrow is decorative; the visually-hidden
 * text is what a screen reader appends to the link's name.
 */
const ExternalHint = () => {
  const { t } = useI18n();
  return (
    <>
      <span class="sr-only">{t("nav_opens_in_new_tab")}</span>
      <TbExternalLink class="ml-auto size-3.5 shrink-0 text-muted-text" aria-hidden />
    </>
  );
};

export const UserAccountPopover: Component<{ onOpenOrganizations: () => void }> = (props) => {
  const wire = useWire();
  const { t } = useI18n();
  const scope = useOrgScope();

  const isRegistered = () => wire.auth?.isRegistered() ?? false;

  // The owner rows aim at an organization the user owns, which is not always
  // the one the header names.
  const ownerOrg = () => scope.defaultOwnedOrganization();

  return (
    <>
      <Show
        when={isRegistered()}
        fallback={
          <>
            <div class="flex flex-col gap-2 px-2 py-1.5">
              <A
                href="/auth/login"
                class="w-full rounded bg-brand px-4 py-2 text-center font-medium text-on-brand transition-colors hover:bg-brand-hover"
              >
                {t("button_log_in")}
              </A>
              <A
                href="/auth/signup"
                class="w-full rounded border border-primary-border px-4 py-2 text-center font-medium text-primary-text transition-colors hover:bg-canvas-hover hover:text-brand"
              >
                {t("action.sign_up")}
              </A>
            </div>
            <div class="px-2 py-1.5">
              <span class="text-sm">
                <span class="font-bold">{t("settings_plan")}</span>{" "}
                <A href="/pricing" class="text-primary-text transition-colors hover:text-brand hover:underline">
                  {t("pricing_plan_explore")}
                </A>
              </span>
            </div>
          </>
        }
      >
        <Show
          when={scope.isMultiOrg() && scope.currentOrganization()}
          fallback={
            <div class="px-2 py-1.5">
              <div
                class="truncate text-sm font-medium text-primary-text"
                title={wire.auth?.user()?.displayName || undefined}
              >
                {wire.auth?.user()?.displayName || t("common.you")}
              </div>
              <Show when={scope.currentOrganization()}>
                {(current) => <div class="truncate text-xs text-muted-text">{scope.organizationName(current())}</div>}
              </Show>
              <Show when={wire.auth?.user()?.primaryEmail !== wire.auth?.user()?.displayName}>
                <div class="truncate text-xs text-muted-text" title={wire.auth?.user()?.primaryEmail}>
                  {wire.auth?.user()?.primaryEmail}
                </div>
              </Show>
            </div>
          }
        >
          {(current) => <UserAccountOrgSwitcher current={current()} onOpenOrganizations={props.onOpenOrganizations} />}
        </Show>

        <div class="my-1 border-t border-primary-border" />

        <StDropdownItem as={A} href="/settings/general" class={ACCOUNT_MENU_LINK_ROW}>
          <TbSettings class={ACCOUNT_MENU_SVG_ICON} />
          <span class="text-sm">{t("settings.title")}</span>
        </StDropdownItem>
        <Show when={ownerOrg()}>
          {(org) => (
            <>
              {/* Nothing on the rows says where they lead, so an organization other than the one
                  the header names gets a heading. Inviting a colleague into the wrong one only
                  surfaces on the members page. */}
              <Show when={org().organizationId !== scope.currentOrganization()?.organizationId}>
                <div
                  class="truncate px-2 pt-1.5 pb-0.5 text-xs font-medium text-muted-text"
                  title={scope.organizationName(org())}
                >
                  {scope.organizationName(org())}
                </div>
              </Show>
              <StDropdownItem as={A} href={`/orgs/${org().organizationId}/billing`} class={ACCOUNT_MENU_LINK_ROW}>
                <TbCreditCard class={ACCOUNT_MENU_SVG_ICON} />
                <span class="text-sm">{t("settings.billing")}</span>
              </StDropdownItem>
              <StDropdownItem as={A} href={`/orgs/${org().organizationId}`} class={ACCOUNT_MENU_LINK_ROW}>
                <TbBuildingCommunity class={ACCOUNT_MENU_SVG_ICON} />
                <span class="text-sm">{t("organization.title")}</span>
              </StDropdownItem>
              <StDropdownItem as={A} href={`/orgs/${org().organizationId}/members`} class={ACCOUNT_MENU_LINK_ROW}>
                <TbUserPlus class={ACCOUNT_MENU_SVG_ICON} />
                <span class="text-sm">{t("organization.switcher.invite_members")}</span>
              </StDropdownItem>
              <Show when={scope.hasDefaultName(org())}>
                <StDropdownItem as={A} href={`/orgs/${org().organizationId}/projects`} class={ACCOUNT_MENU_LINK_ROW}>
                  <TbPencil class={ACCOUNT_MENU_SVG_ICON} />
                  <span class="text-sm">{t("organization.switcher.rename_prompt")}</span>
                </StDropdownItem>
              </Show>
            </>
          )}
        </Show>
        <StDropdownItem
          as="a"
          href={SUPPORT_MAILTO}
          onClick={() => trackFeature({ feature: "nav.help", via: "button" })}
          class={ACCOUNT_MENU_LINK_ROW}
        >
          <TbHelp class={ACCOUNT_MENU_SVG_ICON} />
          <span class="text-sm">{t("nav_get_help")}</span>
        </StDropdownItem>
        <StDropdownItem
          as="a"
          href="https://chief.bot/changelog"
          target="_blank"
          rel="noopener noreferrer"
          class={ACCOUNT_MENU_LINK_ROW}
        >
          <TbGift class={ACCOUNT_MENU_SVG_ICON} />
          <span class="text-sm">{t("nav_latest_changes")}</span>
          <ExternalHint />
        </StDropdownItem>
        <StDropdownItem as={A} href="/?landing" class={ACCOUNT_MENU_LINK_ROW}>
          <TbWorld class={ACCOUNT_MENU_SVG_ICON} />
          <span class="text-sm">{t("nav_chief_website")}</span>
        </StDropdownItem>
        <StDropdownItem
          as="a"
          href="https://docs.chief.bot"
          target="_blank"
          rel="noopener noreferrer"
          class={ACCOUNT_MENU_LINK_ROW}
        >
          <TbTerminal2 class={ACCOUNT_MENU_SVG_ICON} />
          <span class="text-sm">{t("nav_mcp_cli")}</span>
          <ExternalHint />
        </StDropdownItem>

        <div class="my-1 border-t border-primary-border" />

        <StDropdownItem
          onClick={() => {
            wire.auth.methods.signOut().catch((error: unknown) => console.error("Failed to sign out", error));
          }}
          class={ACCOUNT_MENU_LINK_ROW}
        >
          <TbLogout class={ACCOUNT_MENU_SVG_ICON} />
          <span class="text-sm">{t("sign_out")}</span>
        </StDropdownItem>
      </Show>
    </>
  );
};
